import cx from 'classnames'
import { useCombobox } from 'downshift'
import React, {
  forwardRef,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  useImperativeHandle
} from 'react'
import { useFormContext } from 'react-hook-form'

import { FormErrorMessage } from './ErrorMessage'

import './Combobox.css'

interface ComboboxProps<T extends { value: string; name: string }> {
  className?: string
  name: string
  defaultValue?: string
  inputProps?: InputHTMLAttributes<HTMLInputElement> & {
    'data-sel': string
    'data-testid'?: string
  }
  renderSuggestion?: (item: T) => ReactNode
  locationFinderPlaceholder?: string
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>
  disabled?: boolean
  type?: HTMLInputTypeAttribute
  placeholder: string
  children?: React.ReactNode
  items: T[]
  onChange?: (value?: string) => void
  onItemSelect: (item: T, callback: (value: string) => void) => void
}

export const Combobox = forwardRef(function Combobox<
  T extends { value: string; name: string }
>(
  {
    className,
    labelProps,
    name,
    disabled,
    type = 'text',
    locationFinderPlaceholder = '',
    inputProps,
    placeholder,
    children,
    items,
    onItemSelect,
    onChange,
    renderSuggestion
  }: ComboboxProps<T>,
  ref
) {
  const { register, formState, getFieldState, watch } = useFormContext()
  const { isValidating } = formState
  const { error, isTouched } = getFieldState(name, formState)
  const value = watch(name)

  const {
    getInputProps,
    getMenuProps,
    getItemProps,
    isOpen,
    openMenu,
    inputValue,
    setInputValue,
    highlightedIndex,
    setHighlightedIndex,
    reset
  } = useCombobox({
    initialInputValue: value,
    items,
    itemToString: item => item?.name || '',
    onStateChange: ({
      type: actionType,
      inputValue: newValue,
      selectedItem
    }) => {
      if (actionType === useCombobox.stateChangeTypes.InputChange) {
        setHighlightedIndex(0)
        onChange?.(newValue)
      }
      if (actionType === useCombobox.stateChangeTypes.InputKeyDownEnter) {
        if (!inputValue && !selectedItem) {
          openMenu()
          setHighlightedIndex(0)
        }
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        onItemSelect(selectedItem, setInputValue)
      }
      try {
        document?.activeElement?.blur()
      } catch (e) {}
    }
  })

  useImperativeHandle(
    ref,
    () => {
      return {
        resetCombobox() {
          reset()
        }
      }
    },
    [reset]
  )

  const displayError = error?.message as string

  const handleInputFocus = (event: React.FocusEvent<HTMLLabelElement>) => {
    window._wt({
      category: 'input',
      action: 'focus',
      values: [{ type: 'name', value: name }]
    })
    labelProps?.onFocus?.(event)
  }

  return (
    <div className='[ f-pos-rel ] full-width:s' style={{ maxWidth: '480px' }}>
      <label
        className={cx(
          className,
          '[ f-d-bl f-pos-rel f-c-fo-pri f-p-h-020 ] FormText-wrapper',
          displayError && 'f-c-bo-war f-m-b-010',
          disabled && 'FormText--disabled',
          isValidating && 'is-validating',
          (isTouched || value) && !displayError && 'f-c-bo-suc400'
        )}
        {...labelProps}
        onFocus={handleInputFocus}>
        <span
          className={cx('[ f-pos-ab f-c-fo-mut f-t-body ] FormTextLabel', {
            'has-value': value || locationFinderPlaceholder
          })}>
          {placeholder}
        </span>
        <input
          type={type}
          className='[ f-p-a-0 ] FormText'
          placeholder={locationFinderPlaceholder}
          disabled={disabled}
          {...getInputProps({
            ...register(name),
            ...inputProps
          })}
        />
        {children}
      </label>
      <ul
        className={cx(
          'combobox-list-container combobox-list-container--limited',
          isOpen && items.length > 0 && 'combobox-list-container--open'
        )}
        {...getMenuProps()}>
        {isOpen &&
          items.map((item, index) => (
            <li
              key={item.name + item.value}
              {...getItemProps({
                item,
                className: cx(
                  'f-t-mini',
                  highlightedIndex === index &&
                    'f-c-fo-ter combobox-list-item--highlighted'
                )
              })}>
              {renderSuggestion ? renderSuggestion(item) : item.name}
            </li>
          ))}
      </ul>
      {displayError && (
        <FormErrorMessage className='FormError-wrapper f-d-bl f-c-fo-war'>
          {displayError}
        </FormErrorMessage>
      )}
    </div>
  )
})
